/**
 * Guide chat: OpenAI API when key is set; otherwise uses apps from DB (experience + productivity) by subject.
 * Env: LANGUAGE_AGENT_OPENAI_API_KEY
 */

const SYSTEM_PROMPT = `You are a guide for ivori. You ONLY direct users to:
1. App types or experiences: Interview Simulator, Quiz Generator, Application Builder, Case Studies, Explore (marketplace), My Path (dashboard).
2. Tasks: you may suggest adding a new task to their task list or rewriting existing ones.

Respond in JSON only, with this shape:
- "reply": string (short friendly message guiding them)
- "options": array of { "label": string, "link": string } – suggested actions (user will pick one; e.g. [{ "label": "Explore Algorithmic Bias", "link": "/apps-store/123" }])
- "suggest_add_task": optional { "label": string, "link": optional string }
- "suggest_rewrite_tasks": optional array of { "label": string, "link": optional string } (max 3)
- "navigate": optional string (path; if no options, use this as single suggestion)

If the user asks something unrelated to apps or tasks, reply with reply: "I can only help you find the right app or task. Try: Explore apps, Case Studies, Quiz Generator, or ask me to add a task." and no other fields.`;

const SUBJECT_KEYWORDS = {
  'computer science': ['computer science', 'cs', 'computing', 'oxford', 'cambridge', 'tech', 'programming', 'software'],
  'medicine': ['medicine', 'medical', 'doctor', 'clinical', 'health'],
  'law': ['law', 'legal', 'bar', 'solicitor'],
  'investment banking': ['investment', 'banking', 'finance', 'ib'],
  'management consulting': ['consulting', 'management', 'mckinsey', 'bcg'],
  'engineering': ['engineering', 'engineer'],
  'geography': ['geography', 'geo'],
};

function matchSubject(text) {
  const lower = (text || '').toLowerCase();
  for (const [subject, keywords] of Object.entries(SUBJECT_KEYWORDS)) {
    if (keywords.some((k) => lower.includes(k))) return subject;
  }
  return null;
}

function appTypeLabel(app) {
  return app?.app_type === 'case' ? 'Experience' : (app?.app_type === 'tutorial' ? 'Tutorial' : 'Productivity');
}

function guideFromApps(userMessage, apps = []) {
  const experienceApps = (apps || []).filter((a) => a.app_type === 'case');
  const productivityApps = (apps || []).filter((a) => a.app_type === 'tutorial' || a.app_type === 'productivity');
  const subject = matchSubject(userMessage);

  if (subject) {
    const bySubject = (list) => list.filter((a) => (a.subject || '').toLowerCase() === subject.toLowerCase());
    const expMatch = bySubject(experienceApps);
    const prodMatch = bySubject(productivityApps);
    const app = expMatch[0] || prodMatch[0] || experienceApps[0] || productivityApps[0];
    if (app) {
      const typeLabel = appTypeLabel(app);
      return {
        reply: `Here are options for ${(subject || '').toLowerCase()}:`,
        options: [{ label: `Explore ${app.title} (${typeLabel})`, link: `/apps-store/${app.id}` }],
        navigate: `/apps-store/${app.id}`,
      };
    }
  }

  if (experienceApps.length > 0 || productivityApps.length > 0) {
    const app = experienceApps[0] || productivityApps[0];
    const typeLabel = appTypeLabel(app);
    return {
      reply: 'Here are options you can try:',
      options: [{ label: `Explore ${app.title} (${typeLabel})`, link: `/apps-store/${app.id}` }],
      navigate: `/apps-store/${app.id}`,
    };
  }

  return {
    reply: 'Here are options:',
    options: [{ label: 'Browse Explore', link: '/apps-store/marketplace' }],
    navigate: '/apps-store/marketplace',
  };
}

export async function sendGuideMessage(userMessage, currentTasks = [], apps = []) {
  const apiKey =
    process.env.REACT_APP_LANGUAGE_AGENT_OPENAI_API_KEY ||
    process.env.LANGUAGE_AGENT_OPENAI_API_KEY;

  if (!apiKey) {
    return guideFromApps(userMessage, apps);
  }

  const tasksText = currentTasks.length
    ? currentTasks.map((t) => `- ${t.label}${t.link ? ' (' + t.link + ')' : ''}`).join('\n')
    : 'No tasks yet.';

  try {
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: `Current tasks:\n${tasksText}\n\nUser: ${userMessage}` },
        ],
        max_tokens: 400,
        temperature: 0.3,
      }),
    });

    if (!res.ok) {
      const err = await res.text();
      return { reply: 'Guide is temporarily unavailable.', error: true, raw: err };
    }

    const data = await res.json();
    const content = data.choices?.[0]?.message?.content?.trim() || '';
    let parsed = { reply: content };
    try {
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) parsed = JSON.parse(jsonMatch[0]);
    } catch (_) {}
    return parsed;
  } catch (e) {
    return { reply: 'Could not reach guide. Use Quick access below.', error: true };
  }
}
